import { checkInvoice } from '../api';
import { Env } from '@/shared/config/environment';

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_ATTEMPTS = 40;

const state = {
  paymentStatus: null,
  failureMessage: null,
  finalUrl: null,
  isLoadingStatus: false,
  statusError: null,
  isPolling: false,
  pollAttempts: 0,
  pollTimer: null,
};

const mutations = {
  SET_PAYMENT_STATUS(state, paymentStatus) {
    state.paymentStatus = paymentStatus;
  },
  SET_FAILURE_MESSAGE(state, failureMessage) {
    state.failureMessage = failureMessage;
  },
  SET_FINAL_URL(state, finalUrl) {
    state.finalUrl = finalUrl;
  },
  SET_LOADING_STATUS(state, isLoadingStatus) {
    state.isLoadingStatus = isLoadingStatus;
  },
  SET_STATUS_ERROR(state, error) {
    state.statusError = error;
  },
  SET_POLLING(state, isPolling) {
    state.isPolling = isPolling;
  },
  INCREMENT_POLL_ATTEMPTS(state) {
    state.pollAttempts++;
  },
  RESET_POLL_ATTEMPTS(state) {
    state.pollAttempts = 0;
  },
  SET_POLL_TIMER(state, timer) {
    state.pollTimer = timer;
  },
};

const actions = {
  async checkPaymentStatus({ commit, dispatch }, invoiceId) {
    commit('SET_LOADING_STATUS', true);
    commit('SET_STATUS_ERROR', null);
    commit('SET_FAILURE_MESSAGE', null);
    commit('SET_FINAL_URL', null);

    try {
      const response = await checkInvoice(invoiceId);
      commit('SET_PAYMENT_STATUS', response?.status);
      commit('SET_FAILURE_MESSAGE', response?.message || null);
      commit('SET_FINAL_URL', response?.final_url || null);

      if (response?.status === 'waiting') {
        dispatch('startPolling', invoiceId);
      }

      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message
        || error.response?.data?.error
        || error.message
        || 'Не вдалося перевірити статус оплати';

      commit('SET_STATUS_ERROR', errorMessage);
      commit('SET_PAYMENT_STATUS', 'fail');
      commit('SET_FAILURE_MESSAGE', errorMessage);
      if (Env.features.showNetworkErrorMessages) {
        dispatch('notificationModule/showNotificationErrorMessage', {
          error: error,
          title: 'Не вдалося перевірити статус оплати',
          message: errorMessage,
        }, { root: true });
      }

      throw error;
    } finally {
      commit('SET_LOADING_STATUS', false);
    }
  },
  startPolling({ commit, dispatch, state }, invoiceId) {
    if (state.pollTimer) {
      clearInterval(state.pollTimer);
    }

    commit('RESET_POLL_ATTEMPTS');
    commit('SET_POLLING', true);

    const timer = setInterval(async () => {
      commit('INCREMENT_POLL_ATTEMPTS');

      if (state.pollAttempts > MAX_POLL_ATTEMPTS) {
        dispatch('stopPolling');
        commit('SET_PAYMENT_STATUS', 'fail');
        commit('SET_FAILURE_MESSAGE', 'Час очікування підтвердження оплати минув. Спробуйте ще раз або перевірте статус пізніше.');
        return;
      }

      try {
        const response = await checkInvoice(invoiceId);
        commit('SET_PAYMENT_STATUS', response?.status);
        commit('SET_FAILURE_MESSAGE', response?.message || null);
        commit('SET_FINAL_URL', response?.final_url || null);

        if (response?.status === 'success' || response?.status === 'fail') {
          dispatch('stopPolling');
        }
      } catch {
        dispatch('stopPolling');
        commit('SET_PAYMENT_STATUS', 'fail');
        commit('SET_FAILURE_MESSAGE', 'Не вдалося перевірити статус оплати.');
      }
    }, POLL_INTERVAL_MS);

    commit('SET_POLL_TIMER', timer);
  },
  stopPolling({ commit, state }) {
    if (state.pollTimer) {
      clearInterval(state.pollTimer);
    }
    commit('SET_POLL_TIMER', null);
    commit('SET_POLLING', false);
    commit('RESET_POLL_ATTEMPTS');
  },
};

const getters = {
  isLoadingStatus: (state) => state.isLoadingStatus,
  statusError: (state) => state.statusError,
  failureMessage: (state) => state.failureMessage,
  finalUrl: (state) => state.finalUrl,
  isSuccess: (state) => state.paymentStatus === 'success',
  isFailed: (state) => state.paymentStatus === 'fail',
  isWaiting: (state) => state.paymentStatus === 'waiting',
  isPolling: (state) => state.isPolling,
  pollAttempts: (state) => state.pollAttempts,
  isStatusResolved: (state) =>
    (state.paymentStatus === 'success' || state.paymentStatus === 'fail') && !state.isLoadingStatus,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
